import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import "../AdminDashboard/AdminDashboard.css";
import "./AdminReminders.css";

const emptyForm = () => ({
  title: "",
  description: "",
  start_date: new Date().toLocaleDateString("en-CA"),
  end_date: "",
  remind_days: 7,
});

const stateLabel = (row) => {
  if (row.state === "expired") {
    const days = Math.abs(row.days_left);
    return days === 0 ? "Expired today" : `Expired ${days} day${days === 1 ? "" : "s"} ago`;
  }
  if (row.state === "due") {
    if (row.days_left === 0) return "Expires today";
    return `Expires in ${row.days_left} day${row.days_left === 1 ? "" : "s"}`;
  }
  return "Scheduled";
};

export function ReminderBanner() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let live = true;
    const load = (attempt) => {
      UserServices.adminReminders({ attention: 1 })
        .then((res) => {
          if (live) setRows(res.data?.data || []);
        })
        .catch(() => {
          if (!live) return;
          if (attempt < 2) {
            window.setTimeout(() => load(attempt + 1), 1200);
            return;
          }
          setRows([]);
        });
    };
    load(0);
    return () => {
      live = false;
    };
  }, []);

  if (!rows.length) return null;

  const expired = rows.some((row) => row.state === "expired");

  return (
    <section className={`ar-banner${expired ? " is-expired" : " is-due"}`}>
      <div className="ar-banner-head">
        <strong>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {expired ? "Something has expired" : "Renewals need attention"}
        </strong>
        <Link to="/admin-reminders">Manage reminders</Link>
      </div>
      <div className="ar-banner-list">
        {rows.map((row) => (
          <article key={row.id} className={`ar-banner-item is-${row.state}`}>
            <div>
              <h3>{row.title}</h3>
              {row.description ? <p>{row.description}</p> : null}
            </div>
            <div className="ar-banner-meta">
              <span>Ends {row.end_date}</span>
              <strong>{stateLabel(row)}</strong>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function AdminReminders() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await UserServices.adminReminders();
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load reminders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.start_date || !form.end_date) {
      toast.error("Title, start date, and end date are required");
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after the start date");
      return;
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      start_date: form.start_date,
      end_date: form.end_date,
      remind_days: Number(form.remind_days) || 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        await UserServices.updateAdminReminder(editingId, payload);
        toast.success("Reminder updated");
      } else {
        await UserServices.createAdminReminder(payload);
        toast.success("Reminder added");
      }
      setForm(emptyForm());
      setEditingId(null);
      load();
    } catch (err) {
      const message = err.response?.data?.message
        || Object.values(err.response?.data?.errors || {})?.[0]?.[0]
        || "Could not save the reminder";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const edit = (row) => {
    setEditingId(row.id);
    setForm({
      title: row.title || "",
      description: row.description || "",
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      remind_days: row.remind_days ?? 7,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove “${row.title}”?`)) return;
    try {
      await UserServices.deleteAdminReminder(row.id);
      toast.success("Reminder removed");
      if (editingId === row.id) {
        setEditingId(null);
        setForm(emptyForm());
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not remove the reminder");
    }
  };

  return (
    <div className="wrapper">
      <ToastContainer />
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ad-topbar">
              <div>
                <p className="ad-eyebrow">Super Admin</p>
                <h2 className="ad-title">
                  <i className="bi bi-bell me-2"></i>
                  Reminders
                </h2>
                <p className="ad-subtitle">
                  Licenses, domain renewals, sign boards, and anything else with an end date.
                </p>
              </div>
            </div>

            <form className="ar-form card border-0 shadow-sm" onSubmit={save}>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Title</label>
                    <input
                      className="form-control"
                      value={form.title}
                      onChange={(e) => onChange("title", e.target.value)}
                      placeholder="Domain renewal, trade license, sign board…"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.description}
                      onChange={(e) => onChange("description", e.target.value)}
                      placeholder="What needs to be renewed, and who handles it"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Start date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.start_date}
                      onChange={(e) => onChange("start_date", e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">End date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.end_date}
                      onChange={(e) => onChange("end_date", e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Remind this many days before the end date</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={form.remind_days}
                      onChange={(e) => onChange("remind_days", e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving…" : editingId ? "Update reminder" : "Add reminder"}
                  </button>
                  {editingId ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setEditingId(null);
                        setForm(emptyForm());
                      }}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              </div>
            </form>

            <div className="card border-0 shadow-sm mt-3">
              <div className="table-responsive">
                <table className="table mb-0 ar-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Warn before</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">Loading…</td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">No reminders yet.</td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <strong>{row.title}</strong>
                            {row.description ? <div className="small text-muted">{row.description}</div> : null}
                          </td>
                          <td>{row.start_date}</td>
                          <td>{row.end_date}</td>
                          <td>{row.remind_days} days</td>
                          <td>
                            <span className={`ar-tag is-${row.state || "scheduled"}`}>{stateLabel(row)}</span>
                          </td>
                          <td className="text-nowrap">
                            <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => edit(row)}>
                              Edit
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(row)}>
                              Del
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
